import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Stack,
  Collapse,
} from '@mui/material';
import {
  Login,
  Logout,
  PersonAdd,
  Edit,
  Email,
  CreditCard,
  CheckCircle,
  Info,
  Refresh,
  AccessTime,
  ExpandMore,
  ExpandLess,
  MoreHoriz,
} from '@mui/icons-material';
import { formatDistanceToNow, parseISO } from 'date-fns';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';

interface Activity {
  id: string;
  timestamp: string;
  user_name?: string;
  activity_type: string;
  activity_level: 'info' | 'success' | 'warning' | 'error';
  club_name?: string;
  title: string;
  description?: string;
}

interface CompactActivityLogProps {
  limit?: number;
}

const CompactActivityLog: React.FC<CompactActivityLogProps> = ({ limit = 5 }) => {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/activities?limit=${limit}`);
      setActivities(response.data);
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActivityIcon = (type: string) => {
    const iconMap: { [key: string]: React.ReactElement } = {
      user_login: <Login sx={{ fontSize: 16 }} />,
      user_logout: <Logout sx={{ fontSize: 16 }} />,
      user_created: <PersonAdd sx={{ fontSize: 16 }} />,
      user_updated: <Edit sx={{ fontSize: 16 }} />,
      card_generated: <CreditCard sx={{ fontSize: 16 }} />,
      card_email_sent: <Email sx={{ fontSize: 16 }} />,
      import_completed: <CheckCircle sx={{ fontSize: 16 }} />,
    };
    return iconMap[type] || <Info sx={{ fontSize: 16 }} />;
  };

  const getLevelColor = (level: string) => {
    const colorMap: { [key: string]: 'default' | 'success' | 'warning' | 'error' } = {
      info: 'default',
      success: 'success',
      warning: 'warning',
      error: 'error',
    };
    return colorMap[level] || 'default';
  };

  const formatTime = (timestamp: string) => {
    return formatDistanceToNow(parseISO(timestamp), { addSuffix: true });
  };

  if (loading) {
    return (
      <Paper sx={{ p: 2, height: 120, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress size={24} />
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccessTime fontSize="small" color="action" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Recent Activity
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchActivities}>
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View all activities">
            <IconButton size="small" onClick={() => navigate('/app/admin/activities')}>
              <MoreHoriz fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded} collapsedSize={180}>
        {activities.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            No recent activities
          </Typography>
        ) : (
          <Stack spacing={0.5}>
            {activities.map((activity) => (
              <Box
                key={activity.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    backgroundColor: `${getLevelColor(activity.activity_level)}.lighter`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {getActivityIcon(activity.activity_type)}
                </Box>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.813rem',
                      lineHeight: 1.3,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <Box component="span" sx={{ fontWeight: 500 }}>
                      {activity.title}
                    </Box>
                    {activity.user_name && (
                      <Box component="span" sx={{ color: 'text.secondary', ml: 0.5 }}>
                        • {activity.user_name}
                      </Box>
                    )}
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  {formatTime(activity.timestamp)}
                </Typography>
              </Box>
            ))}
          </Stack>
        )}
      </Collapse>

      {!expanded && activities.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {activities.slice(0, 3).map((activity) => (
            <Chip
              key={activity.id}
              icon={getActivityIcon(activity.activity_type)}
              label={activity.title}
              size="small"
              color={getLevelColor(activity.activity_level)}
              sx={{
                height: 22,
                fontSize: '0.75rem',
                '& .MuiChip-label': {
                  px: 1,
                  maxWidth: 120,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            />
          ))}
          {activities.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              +{activities.length - 3} more
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CompactActivityLog;